import type { ItemRoleNullable, ItemType } from "../utils";
import type { ThreadAiEventsSubscription, ThreadAskAiMutationVariables, ThreadQuery } from "./queries";

export type ThreadData = NonNullable<ThreadQuery["thread"]>;
export type ThreadPersonality = NonNullable<ThreadData["aiPersonality"]>;

export interface ThreadItemModel {
    key: string;
    id: number | null;
    type: ItemType;
    role: ItemRoleNullable;
    content: string;
    toolName: string | null;
    toolArguments: string | null;
    toolCallId: string | null;
    reasoningSummary: string[];
    context: boolean;
    threadItemRetrievalContext: {
        id: number;
        keywordQuery: string;
        semanticQuery: string;
        hydePassage: string;
        avgRelevance: number;
        documentNames: string[];
    } | null;
    threadItemReadContext: {
        id: number;
        documentId: number;
        documentName: string;
        startPart: number;
        endPart: number;
        totalParts: number;
    } | null;
    threadItemBrainstormContext: {
        id: number;
        what: string;
        count: number;
        candidates: string[];
        probabilities: number[];
        selectedIndices: number[];
        epsilon: number;
    } | null;
    file: {
        fileType: string;
        fileName: string;
        src: string;
    } | null;
}

type ThreadItemNode = {
    id: number | null;
    type: ItemType;
    role: ItemRoleNullable;
    content: string;
    toolName: string | null;
    toolArguments: string | null;
    toolCallId: string | null;
    reasoningSummary?: string[] | null;
    context: boolean;
    threadItemRetrievalContext?: {
        id?: number;
        keywordQuery: string;
        semanticQuery: string;
        hydePassage: string;
        avgRelevance: number;
        documentNames: string[];
    } | null;
    threadItemReadContext?: {
        id?: number;
        documentId: number;
        documentName: string;
        startPart: number;
        endPart: number;
        totalParts: number;
    } | null;
    threadItemBrainstormContext?: {
        id?: number;
        what: string;
        count: number;
        candidates: string[];
        probabilities: number[];
        selectedIndices: number[];
        epsilon: number;
    } | null;
    file?: {
        fileType: string;
        fileName: string;
        src: string;
    } | null;
};

type ThreadItemsConnection<TNode extends ThreadItemNode> = {
    edges: Array<{
        node: TNode;
    }>;
};

function mapRetrievalContext(
    context: ThreadItemNode["threadItemRetrievalContext"],
): ThreadItemModel["threadItemRetrievalContext"] {
    if (!context) {
        return null;
    }

    return {
        id: context.id ?? -1,
        keywordQuery: context.keywordQuery,
        semanticQuery: context.semanticQuery,
        hydePassage: context.hydePassage,
        avgRelevance: context.avgRelevance,
        documentNames: context.documentNames,
    };
}

function mapReadContext(context: ThreadItemNode["threadItemReadContext"]): ThreadItemModel["threadItemReadContext"] {
    if (!context) {
        return null;
    }

    return {
        id: context.id ?? -1,
        documentId: context.documentId,
        documentName: context.documentName,
        startPart: context.startPart,
        endPart: context.endPart,
        totalParts: context.totalParts,
    };
}

function mapBrainstormContext(
    context: ThreadItemNode["threadItemBrainstormContext"],
): ThreadItemModel["threadItemBrainstormContext"] {
    if (!context) {
        return null;
    }

    return {
        id: context.id ?? -1,
        what: context.what,
        count: context.count,
        candidates: context.candidates,
        probabilities: context.probabilities,
        selectedIndices: context.selectedIndices,
        epsilon: context.epsilon,
    };
}

function getPersistedThreadItemKey(id: number | null): string {
    return `thread-item:${id}`;
}

export function mapThreadNodeToThreadItem<TNode extends ThreadItemNode>(node: TNode): ThreadItemModel {
    return {
        key: getPersistedThreadItemKey(node.id),
        id: node.id,
        type: node.type,
        role: node.role,
        content: node.content,
        toolCallId: node.toolCallId,
        toolName: node.toolName,
        toolArguments: node.toolArguments,
        reasoningSummary: node.reasoningSummary ?? [],
        context: node.context,
        threadItemRetrievalContext: mapRetrievalContext(node.threadItemRetrievalContext),
        threadItemReadContext: mapReadContext(node.threadItemReadContext),
        threadItemBrainstormContext: mapBrainstormContext(node.threadItemBrainstormContext),
        file: node.file ?? null,
    };
}

export function mapLiveItemToThreadItem(
    item: NonNullable<ThreadAiEventsSubscription["threadAiEvents"]["item"]>,
    localKey: string,
): ThreadItemModel {
    return {
        key: localKey,
        id: item.id,
        type: item.type as ItemType,
        role: item.role as ItemRoleNullable,
        content: item.content,
        toolCallId: item.toolCallId,
        toolName: item.toolName,
        toolArguments: item.toolArguments,
        reasoningSummary: item.reasoningSummary ?? [],
        context: item.context,
        threadItemRetrievalContext: mapRetrievalContext(item.threadItemRetrievalContext),
        threadItemReadContext: mapReadContext(item.threadItemReadContext),
        threadItemBrainstormContext: mapBrainstormContext(item.threadItemBrainstormContext),
        file: null,
    };
}

export function createOptimisticUserItem(content: string, localKey: string): ThreadItemModel {
    return {
        key: localKey,
        id: null,
        type: "MESSAGE",
        role: "USER",
        content,
        context: false,
        toolCallId: null,
        toolName: null,
        toolArguments: null,
        reasoningSummary: [],
        threadItemRetrievalContext: null,
        threadItemReadContext: null,
        threadItemBrainstormContext: null,
        file: null,
    };
}

export function buildPersonalityInput(
    personality: ThreadPersonality,
): ThreadAskAiMutationVariables["data"]["personalityData"] {
    return {
        name: personality.name,
        externalName: personality.externalName,
        temperature: personality.temperature,
        systemPrompt: personality.systemPrompt,
        includeRetrieval: personality.includeRetrieval,
        includeBrainstorm: personality.includeBrainstorm,
        includeMissingInformation: personality.includeMissingInformation,
        epsilon: personality.epsilon,
        tags: personality.tags,
        languages: personality.languages,
        model: personality.model,
        useCache: personality.useCache,
        showInChat: null,
        reasoningEffort: personality.reasoningEffort,
        verbosity: personality.verbosity,
        responseFormat: personality.responseFormat,
        maxIterations: personality.maxIterations,
    };
}

export function replaceThreadItemsFromConnection<TNode extends ThreadItemNode>(
    threadItems: ThreadItemsConnection<TNode>,
): ThreadItemModel[] {
    return threadItems.edges.map(({ node }) => mapThreadNodeToThreadItem(node));
}
